import React, { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useFragment } from 'react-relay';

import type {
	ViewPageConversation_commentThreads$data,
	ViewPageConversation_commentThreads$key,
} from './__generated__/ViewPageConversation_commentThreads.graphql.ts';
import type { ViewPageConversation_contentPermission$key } from './__generated__/ViewPageConversation_contentPermission.graphql.ts';
import type { ViewPageConversation_currentUser$key } from './__generated__/ViewPageConversation_currentUser.graphql.ts';
import {
	ViewPageConversationCommentThreadsFragment,
	ViewPageConversationContentPermissionFragment,
	ViewPageConversationCurrentUserFragment,
} from './ViewPageConversation.ts';

type ConversationCurrentUser = {
	id: string | null;
	displayName: string | null;
	photos: ReadonlyArray<{
		isPrimary: boolean | null;
		value: string | null;
	} | null> | null;
};

type ViewPageConversationDataValue = {
	canAddComments?: boolean;
	currentUser?: ConversationCurrentUser | null;
};

const ViewPageConversationDataContext = createContext<ViewPageConversationDataValue>({});

export const useViewPageConversationData = (): ViewPageConversationDataValue =>
	useContext(ViewPageConversationDataContext);

export const useViewPageConversationCommentThreads = (
	commentThreadsFragmentRef: ViewPageConversation_commentThreads$key | null | undefined,
): ViewPageConversation_commentThreads$data | null | undefined =>
	useFragment<ViewPageConversation_commentThreads$key>(
		ViewPageConversationCommentThreadsFragment,
		commentThreadsFragmentRef,
	);

export const ViewPageConversationDataProvider = ({
	contentPermissionFragmentRef,
	currentUserFragmentRef,
	children,
}: {
	contentPermissionFragmentRef: ViewPageConversation_contentPermission$key;
	currentUserFragmentRef: ViewPageConversation_currentUser$key;
	children?: ReactNode;
}): React.JSX.Element => {
	const contentPermission = useFragment<ViewPageConversation_contentPermission$key>(
		ViewPageConversationContentPermissionFragment,
		contentPermissionFragmentRef,
	);
	const currentUserData = useFragment<ViewPageConversation_currentUser$key>(
		ViewPageConversationCurrentUserFragment,
		currentUserFragmentRef,
	);

	const value = useMemo<ViewPageConversationDataValue>(
		() => ({
			canAddComments: (contentPermission.operations ?? []).some(
				(operation) => operation?.operation === 'create' && operation.targetType === 'comment',
			),
			currentUser: currentUserData.user,
		}),
		[contentPermission.operations, currentUserData.user],
	);

	return (
		<ViewPageConversationDataContext.Provider value={value}>
			{children}
		</ViewPageConversationDataContext.Provider>
	);
};
